import { formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { EffectiveRate, FundKpis, Member, PensionAccount, PolicyLimitsResponse } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, PageHeader, Spinner, StatCard } from '../../ui/components';

function hasAny(roles: string[], ...wanted: string[]): boolean {
  return wanted.some((w) => roles.includes(w));
}

export function StaffDashboard() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];

  const canSeeKpis = hasAny(roles, 'SPF_CEO', 'SPF_ADMIN', 'FSA_SUPERVISOR');
  const canSeeMembers = hasAny(roles, 'SPF_OFFICER', 'SPF_ADMIN');
  const canSeePensioners = hasAny(roles, 'SPF_BENEFITS_OFFICER', 'SPF_ADMIN');
  const canSeePortfolio = hasAny(roles, 'SPF_INVESTMENT_OFFICER', 'SPF_PROPERTY_MANAGER', 'SPF_BOARD', 'SPF_ADMIN');

  // /rates/effective has no role gate (any authenticated T1 staff can read it).
  const rateQ = useApiQuery<EffectiveRate>('/api/v1/actuarial/rates/effective');
  const kpisQ = useApiQuery<FundKpis>(canSeeKpis ? '/api/v1/actuarial/kpis' : null);
  const membersQ = useApiQuery<{ items: Member[] }>(canSeeMembers ? '/api/v1/members?limit=5' : null);
  const pensionersQ = useApiQuery<{ items: PensionAccount[] }>(
    canSeePensioners ? '/api/v1/benefits/pensioners?status=IN_PAYMENT' : null,
  );
  const policyQ = useApiQuery<PolicyLimitsResponse>(canSeePortfolio ? '/api/v1/investments/compliance/policy-limits' : null);

  const nothingToShow = !canSeeKpis && !canSeeMembers && !canSeePensioners && !canSeePortfolio;

  return (
    <div>
      <PageHeader title="SPF back-office dashboard" subtitle={`Signed in as ${persona?.title} — ${persona?.org}`} />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Legislated contribution rate"
          value={rateQ.loading ? '…' : rateQ.data ? `${rateQ.data.employeePct}% + ${rateQ.data.employerPct}%` : '—'}
          hint={rateQ.data ? `Effective ${formatDate(rateQ.data.effectiveFrom)} · ${rateQ.data.legalRef}` : 'RateSchedule (P06)'}
        />
        {canSeeKpis ? (
          <StatCard
            label="Latest funding ratio"
            value={kpisQ.loading ? '…' : kpisQ.data?.latestFundingRatio != null ? kpisQ.data.latestFundingRatio.toFixed(2) : '—'}
            hint="Assets / liabilities"
          />
        ) : null}
        {canSeeKpis ? (
          <StatCard
            label="Contributor : pensioner ratio"
            value={
              kpisQ.loading ? '…' : kpisQ.data?.contributorPensionerRatio != null ? kpisQ.data.contributorPensionerRatio.toFixed(2) : '—'
            }
          />
        ) : null}
        {canSeePortfolio ? (
          <StatCard
            label="Total portfolio market value"
            value={policyQ.loading ? '…' : policyQ.data ? scr(policyQ.data.totalMarketValue) : '—'}
            hint="Investments & property (P05)"
          />
        ) : null}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {canSeeMembers ? (
          <div className="card p-4">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Recently registered members</h2>
            {membersQ.loading ? (
              <Spinner />
            ) : (membersQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No members registered yet" />
            ) : (
              <ul className="space-y-2 text-sm">
                {membersQ.data!.items.map((m) => (
                  <li key={m.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                    <span>{m.fullName}</span>
                    <span className="text-xs text-slate-400">{m.nin}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {canSeePensioners ? (
          <div className="card p-4">
            <h2 className="mb-3 text-sm font-bold text-slate-700">Pensioners in payment</h2>
            {pensionersQ.loading ? (
              <Spinner />
            ) : (
              <>
                <div className="text-3xl font-bold tabular-nums text-seagreen-600">{pensionersQ.data?.items.length ?? 0}</div>
                <p className="mt-1 text-xs text-slate-500">
                  Total monthly payroll: {scr((pensionersQ.data?.items ?? []).reduce((s, a) => s + Number(a.monthlyAmount), 0))}
                </p>
              </>
            )}
          </div>
        ) : null}
      </div>

      {nothingToShow ? (
        <EmptyState title="Nothing to show for your role yet" hint="Use the sidebar to navigate to the module you work in." />
      ) : null}
    </div>
  );
}
